import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../App';
import { Menu } from 'lucide-react';
import { AppLayout } from './AppLayout';

interface MainShellProps {
  active: string;
  onMenuClick?: (id: string) => void;
  children: React.ReactNode;
}

// simple hook
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

const MainShell: React.FC<MainShellProps> = ({ active, children, onMenuClick }) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const handleSelect = (id: string) => {
    if (onMenuClick) {
      onMenuClick(id);
    } else {
      switch (id) {
        case 'dashboard': navigate('/'); break;
        case 'reviews': navigate('/reviews'); break;
        case 'analytics': navigate('/analytics'); break;
        case 'notifications': navigate('/notifications'); break;
        case 'settings': navigate('/settings'); break;
        default: navigate('/');
      }
    }
    setOpen(false);
  };

  return (
    <AppLayout>
      <div className="flex h-screen bg-background md:overflow-hidden">
        {/* mobile top bar */}
        {isMobile && (
          <div className="fixed top-0 left-0 w-full h-12 bg-background border-b flex items-center px-4 z-40 md:hidden">
            <button onClick={() => setOpen(true)} className="mr-2">
              <Menu className="w-6 h-6" />
            </button>
            <span className="font-bold">Approvia</span>
          </div>
        )}
        {/* sidebar */}
        {!isMobile && <Sidebar activeItem={active} onMenuClick={handleSelect} />}
        {isMobile && (
          <div className={`fixed inset-y-0 left-0 w-64 bg-background border-r z-50 transform ${open ? 'translate-x-0' : '-translate-x-full'} transition-transform md:hidden`}>
            <Sidebar mobile activeItem={active} onMenuClick={handleSelect} />
          </div>
        )}
        {isMobile && open && <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setOpen(false)} />}

        {/* main */}
        <div className={`flex-1 overflow-auto ${isMobile ? 'pt-12' : ''}`}>{children}</div>
      </div>
    </AppLayout>
  );
};

export default MainShell; 